import { useState, useEffect } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp, FaFileExcel } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import { Form, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import * as apicall from '../../components/Apicall';

const SubscriptionList = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('active');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState({});
  const [detailsData, setDetailsData] = useState({});
  const [detailsLoading, setDetailsLoading] = useState({});
  const [exporting, setExporting] = useState(false);
  const rowsPerPage = 25;

  const fetchSubscriptions = async () => {
    setLoadingSubscriptions(true);
    try {
      const res = await apicall.post({}, 'ordersubscription');
      if (res.success) {
        setSubscriptions(res.data);
        setFilteredSubscriptions(res.data);
      } else {
        toast.error('Failed to fetch subscriptions.');
      }
    } catch (e) {
      toast.error('Failed to fetch subscriptions.');
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [activeFilter, searchTerm, subscriptions]);

  const applyFilters = () => {
    let filtered = [...subscriptions];
    if (activeFilter === 'active') {
      filtered = filtered.filter(s => s.is_active === 1 || s.is_active === true);
    } else if (activeFilter === 'inactive') {
      filtered = filtered.filter(s => s.is_active === 0 || s.is_active === false);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter(s =>
        Object.values(s).some(val => val?.toString().toLowerCase().includes(term))
      );
    }
    setFilteredSubscriptions(filtered);
    setCurrentPage(1);
  };

  const handleSearch = () => applyFilters();

  const handleViewToggle = async (sub) => {
    const id = sub.id;
    const isExpanded = expandedRows[id];

    if (isExpanded) {
      setExpandedRows(prev => ({ ...prev, [id]: false }));
      return;
    }

    setExpandedRows(prev => ({ ...prev, [id]: true }));

    if (detailsData[id]) return; // already loaded

    setDetailsLoading(prev => ({ ...prev, [id]: true }));
    try {
      const res = await apicall.post({ subscription_id: id }, 'ordersubscriptiondetails');
      setDetailsData(prev => ({ ...prev, [id]: res.data || [] }));
    } catch (e) {
      toast.error('Failed to fetch subscription details.');
      setDetailsData(prev => ({ ...prev, [id]: [] }));
    } finally {
      setDetailsLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const fetchDetails = async (id) => {
    if (detailsData[id]) return detailsData[id];
    try {
      const res = await apicall.post({ subscription_id: id }, 'ordersubscriptiondetails');
      return res.data || [];
    } catch (e) {
      return [];
    }
  };

  const exportToExcel = async () => {
    if (!filteredSubscriptions.length) {
      toast.info('No data to export!');
      return;
    }
    setExporting(true);
    try {
      const allDetails = await Promise.all(
        filteredSubscriptions.map(sub => fetchDetails(sub.id))
      );
      const excludedStatuses = ['Worker Assigned', 'Vendor Assigned', 'Vendor Not Assigned'];
      const allStatuses = [...new Set(allDetails.flat().map(row => row.status).filter(Boolean))]
        .filter(status => !excludedStatuses.includes(status));

      const subscriptionRows = filteredSubscriptions.map((sub, index) => {
        const bookings = allDetails[index];
        const firstBooking = bookings[0] || {};
        const statusCounts = Object.fromEntries(
          allStatuses.map(status => [status, bookings.filter(row => row.status === status).length])
        );
        return {
          'Sr No': index + 1,
          'ID': sub.id,
          'User Name': sub.first_name || '-',
          'Phone Number': sub.phone_number || '-',
          'Service': firstBooking.service || '-',
          'Service Type': firstBooking.service_type || '-',
          'Service Offering': firstBooking.service_offering || '-',
          'Service State': firstBooking.service_state || '-',
          'Frequency': sub.frequency || '-',
          'Service Day': sub.service_day || '-',
          'Type': sub.type || '-',
          'Start Date': formatDate(sub.start_date),
          'Time': sub.time || '-',
          'Status': sub.is_active === 1 || sub.is_active === true ? 'Active' : 'Inactive',
          'No. of Visits': bookings.length,
          ...statusCounts,
        };
      });

      const detailRows = filteredSubscriptions.flatMap((sub, i) =>
        allDetails[i].map(row => ({
          'Subscription ID': sub.id,
          'User Name': sub.first_name || '-',
          'Booking ID': row.booking_id || '-',
          'Service': row.service || '-',
          'Service Type': row.service_type || '-',
          'Offering': row.service_offering || '-',
          'Service State': row.service_state || '-',
          'Date': formatDate(row.serviceDate),
          'Time': row.service_time || '-',
          'Status': row.status || '-',
        }))
      );

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(subscriptionRows), 'Subscriptions');
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(detailRows), 'Booking Details');

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(data, `subscriptions_${activeFilter}_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (e) {
      toast.error('Failed to export subscriptions.');
    } finally {
      setExporting(false);
    }
  };

  // Pagination
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentData = filteredSubscriptions.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredSubscriptions.length / rowsPerPage);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusBadge = (isActive) => {
    const active = isActive === 1 || isActive === true;
    return (
      <span style={{
        background: active ? '#e6f4ea' : '#fce8ec',
        color: active ? '#2e7d32' : '#d01c4c',
        padding: '3px 10px',
        borderRadius: '12px',
        fontSize: '13px',
        fontWeight: '500',
      }}>
        {active ? 'Active' : 'Inactive'}
      </span>
    );
  };

  return (
    <div className="vendor-table-container container mt-3">
      {/* Header Row */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3>Subscriptions</h3>
          <p>Here is a list of all Subscriptions</p>
        </div>

        <div className="mx-5 mb-4">
          <InputGroup>
            <Form.Control
              type="search"
              placeholder="Search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: '350px' }}
              onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
            />
            <InputGroup.Text className="primaryBackground" onClick={handleSearch} style={{ cursor: 'pointer' }}>
              <FaSearch className="text-white" />
            </InputGroup.Text>
          </InputGroup>
        </div>

        <div className="d-flex align-items-center mb-3 gap-2">
          <button className={`btn ${activeFilter === 'all' ? 'primaryBackground text-white' : 'btn-outline-secondary'}`} onClick={() => setActiveFilter('all')}>All</button>
          <button className={`btn ${activeFilter === 'active' ? 'primaryBackground text-white' : 'btn-outline-secondary'}`} onClick={() => setActiveFilter('active')}>Active</button>
          <button className={`btn ${activeFilter === 'inactive' ? 'primaryBackground text-white' : 'btn-outline-secondary'}`} onClick={() => setActiveFilter('inactive')}>Inactive</button>
        </div>
      </div>

      <div className="d-flex justify-content-end mb-3">
        <button
          className="btn Button text-black primaryColorText btn-outline-secondary"
          style={{ height: '35px' }}
          onClick={exportToExcel}
          disabled={exporting || loadingSubscriptions}
        >
          <FaFileExcel style={{ color: 'green' }} className="me-2" />
          {exporting ? 'Exporting...' : 'Export'}
        </button>
      </div>

      {/* Table */}
      {loadingSubscriptions ? (
        <Skeleton count={5} height={40} />
      ) : (
        <div>
          <div className="table-responsive" style={{ maxHeight: 600, overflowY: 'auto' }}>
            <table className="table table-hover">
              <thead>
                <tr>
                  <th scope="col">Sr No</th>
                  <th scope="col">ID</th>
                  <th scope="col">User Name</th>
                  <th scope="col">Phone Number</th>
                  <th scope="col">Frequency</th>
                  <th scope="col">Service Day</th>
                  <th scope="col">Type</th>
                  <th scope="col">Start Date</th>
                  <th scope="col">Time</th>
                  <th scope="col">Status</th>
                  <th scope="col">Details</th>
                </tr>
              </thead>
              <tbody>
                {currentData.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center">No Records available.</td>
                  </tr>
                ) : (
                  currentData.map((sub, index) => (
                    <>
                      <tr key={sub.id || index}>
                        <td>{indexOfFirstRow + index + 1}</td>
                        <td>{sub.id || '-'}</td>
                        <td>{sub.first_name || '-'}</td>
                        <td>{sub.phone_number || '-'}</td>
                        <td>{sub.frequency || '-'}</td>
                        <td>{sub.service_day || '-'}</td>
                        <td>{sub.type || '-'}</td>
                        <td>{formatDate(sub.start_date)}</td>
                        <td>{sub.time || '-'}</td>
                        <td>{getStatusBadge(sub.is_active)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                            onClick={() => handleViewToggle(sub)}
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {expandedRows[sub.id] ? <><span>Hide</span><FaChevronUp size={11} /></> : <><span>View</span><FaChevronDown size={11} /></>}
                          </button>
                        </td>
                      </tr>

                      {expandedRows[sub.id] && (
                        <tr key={`detail-${sub.id}`} style={{ background: '#FFF6F6' }}>
                          <td colSpan={11} style={{ padding: '16px 24px' }}>
                            <p className="mb-2" style={{ fontWeight: '600', color: '#d01c4c' }}>Booking Details</p>
                            {detailsLoading[sub.id] ? (
                              <Skeleton count={3} height={30} />
                            ) : detailsData[sub.id]?.length > 0 ? (
                              <table className="table table-sm mb-0" style={{ background: 'white', borderRadius: '8px', overflow: 'hidden' }}>
                                <thead style={{ background: '#f5f5f5' }}>
                                  <tr>
                                    <th>Booking ID</th>
                                    <th>Service</th>
                                    <th>Service Type</th>
                                    <th>Offering</th>
                                    <th>Date</th>
                                    <th>Time</th>
                                    <th>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {detailsData[sub.id].map((row, i) => (
                                    <tr key={row.booking_id || i}>
                                      <td>
                                        {row.booking_id ? (
                                          <span
                                            style={{ color: '#d01c4c', cursor: 'pointer', textDecoration: 'underline' }}
                                            onClick={() => navigate(`/bookingDetails/${row.booking_id}`, { state: { bookingId: row.booking_id } })}
                                          >
                                            {row.booking_id}
                                          </span>
                                        ) : '-'}
                                      </td>
                                      <td>{row.service || '-'}</td>
                                      <td>{row.service_type || '-'}</td>
                                      <td>{row.service_offering || '-'}</td>
                                      <td>{formatDate(row.serviceDate)}</td>
                                      <td>{row.service_time || '-'}</td>
                                      <td>{row.status || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <p className="text-muted mb-0" style={{ fontSize: '14px' }}>No booking details found for this subscription.</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav aria-label="Subscription pagination" className="d-flex justify-content-center mt-3 flex-wrap">
              <ul className="pagination d-flex flex-wrap justify-content-center overflow-auto" style={{ maxWidth: '100%' }}>
                <li className="page-item">
                  <button className="btn primaryColorText btn-outline-secondary text-black m-2 Button" onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>Previous</button>
                </li>
                <li className="page-item">
                  <button className={`btn primaryColorText btn-outline-secondary m-2 p-2 btn-width ${currentPage === 1 ? 'active' : ''}`} onClick={() => setCurrentPage(1)}>1</button>
                </li>
                {currentPage > 3 && (
                  <li className="page-item disabled"><button className="btn primaryColorText btn-outline-secondary m-2 p-2 btn-width">...</button></li>
                )}
                {Array.from({ length: 3 }, (_, i) => currentPage - 1 + i)
                  .filter(p => p > 1 && p < totalPages)
                  .map(p => (
                    <li key={p} className={`page-item ${currentPage === p ? 'active' : ''}`}>
                      <button className="btn primaryColorText btn-outline-secondary m-2 p-2 btn-width" onClick={() => setCurrentPage(p)}>{p}</button>
                    </li>
                  ))}
                {currentPage < totalPages - 2 && (
                  <li className="page-item disabled"><button className="btn primaryColorText btn-outline-secondary m-2 p-2 btn-width">...</button></li>
                )}
                {totalPages > 1 && (
                  <li className="page-item">
                    <button className={`btn primaryColorText btn-outline-secondary m-2 p-2 btn-width ${currentPage === totalPages ? 'active' : ''}`} onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
                  </li>
                )}
                <li className="page-item">
                  <button className="btn primaryColorText btn-outline-secondary text-black m-2 Button" onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>Next</button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionList;
